import React from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ChatWidget } from './components/ChatWidget';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { AgentWhatsAppAiPage } from './pages/AgentWhatsAppAiPage';
import { SmartClaimAiPage } from './pages/SmartClaimAiPage';
import { LegalCloudAiPage } from './pages/LegalCloudAiPage';
import { BiznesFinderAiPage } from './pages/BiznesFinderAiPage';
import { CarHunterAiPage } from './pages/CarHunterAiPage';
import { ContentFlowAiPage } from './pages/ContentFlowAiPage';
import { NewsHunterAiPage } from './pages/NewsHunterAiPage';
import { AmazonListerAiPage } from './pages/AmazonListerAiPage';
import { PerpetuumAiPage } from './pages/PerpetuumAiPage';
import { PayPalSyncAiPage } from './pages/PayPalSyncAiPage';
import { SzkolenieAiKancelariePage } from './pages/SzkolenieAiKancelariePage';
import { SizeHunterAiPage } from './pages/SizeHunterAiPage';
import { GridMasterAiPage } from './pages/GridMasterAiPage';
import { WindSynergyAiPage } from './pages/WindSynergyAiPage';
import { AiQuizPage } from './pages/AiQuizPage';
import { ZapytajChataAiPage } from './pages/ZapytajChataAiPage';
import { FaqPage } from './pages/FaqPage';
import { VisualGeneratorAiPage } from './pages/VisualGeneratorAiPage';
import { IntegracjePage } from './pages/IntegracjePage';
import { CaseStudiesPage } from './pages/CaseStudiesPage';
import { LeadMagnet15PomyslowAiPage } from './pages/LeadMagnet15PomyslowAiPage';
import { TikTokLandingPage } from './pages/TikTokLandingPage';
import { ROICalculatorPage } from './pages/ROICalculatorPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { trackPlausibleEvent } from './utils/tracking';
import { captureOnce as captureUtmOnce } from './utils/utm';
import { WhatsAppButton } from './components/WhatsAppButton';
// 2026-08-12: <CookieBanner /> CELOWO nie jest renderowany. Serwis miał DWA niezależne banery zgód
// robiące to samo: ten komponent i baner w powłoce HTML (public/index.html, #cookie-banner).
// Oba zapisują ten sam klucz `cookie_consent` w localStorage, więc działały zgodnie, ale użytkownik
// widział dwa zestawy przycisków „Odrzuć / Akceptuję" (potwierdzone na produkcji: jeden kontener
// .cookie-banner, dwa przyciski „Akceptuję" — także na stronie głównej, więc to nie regresja z
// landingu, tylko dług sprzed niego). Przy zgodach dublowanie to nie kosmetyka: nie wiadomo, który
// wybór się liczy. Zostaje baner z powłoki, bo to ON jest wpięty w Consent Mode (gtag consent
// default/update w tym samym pliku). Komponent zostawiam w repo — do usunięcia po decyzji, którą
// ścieżkę zgód projekt utrzymuje docelowo.
// import { CookieBanner } from './components/CookieBanner';
import { ProduktyPage } from './pages/ProduktyPage';
import { SzablonyN8nPage } from './pages/SzablonyN8nPage';
import { PromptyPage } from './pages/PromptyPage';
import { PromptyDetailPage } from './pages/PromptyDetailPage';
// Admin (Phase 1.3+)
import { AdminLayout } from './components/admin/AdminLayout';
import { LoginPage as AdminLoginPage } from './pages/admin/LoginPage';
import { LeadsPage as AdminLeadsPage } from './pages/admin/LeadsPage';
import { InboxPage as AdminInboxPage } from './pages/admin/InboxPage';
import { SettingsPage as AdminSettingsPage } from './pages/admin/SettingsPage';
import { PlaceholderPage } from './pages/admin/PlaceholderPage';
import { PropozycjePostowPage } from './pages/admin/PropozycjePostowPage';
import { HarmonogramPage } from './pages/admin/HarmonogramPage';
import { MediaplanPage } from './pages/admin/MediaplanPage';
import { PostyFbIgPage } from './pages/admin/PostyFbIgPage';
import { AktywnoscPage } from './pages/admin/AktywnoscPage';

declare global {
  interface Window {
    plausible: (event: string, options?: { props: Record<string, any> }) => void;
  }
}

// Trasy renderowane BEZ oprawy serwisu (nagłówek z nawigacją, stopka, sekcje globalne, pasek CTA
// do drugiej marki). Patrz isBareLanding niżej.
const BARE_LANDING_ROUTES = ['/tiktok'];

const CtaBar: React.FC = () => {
  return (
    <div 
      id="cta-bar" 
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-900 text-white p-3 text-sm sm:text-base"
      style={{ background: '#111' }}
    >
      <div className="container mx-auto flex gap-3 items-center justify-between flex-wrap">
        <span className="flex-grow text-center sm:text-left sm:flex-grow-0">To jest moje demo‑studio AI. Chcesz gotowy system pod swój biznes?</span>
        <a 
          id="cta-to-offer" 
          href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=cta_bar&utm_campaign=bbk%E2%86%92bg"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackPlausibleEvent('cta_click', { placement: 'bottom_bar' })}
          className="bg-[#00e676] text-gray-900 py-2 px-4 rounded-lg font-bold whitespace-nowrap w-full sm:w-auto text-center hover:opacity-90 transition-opacity"
          style={{ background: '#00e676', color: '#111', textDecoration: 'none' }}
        >
           Zobacz ofertę → BartoszGaca.pl
        </a>
      </div>
    </div>
  );
};

const SiteSchema: React.FC = () => {
    React.useEffect(() => {
        // Multi-schema bundle modeled on the sibling legal-SaaS site (earns Review
        // + FAQ snippets in SERP). BBK is still pre-traction so no AggregateRating yet —
        // adding that without real reviews violates Google guidelines. Adds back when
        // we have ≥10 verified ratings.
        const schemas = [
            {
                "@context": "https://schema.org",
                "@type": "Organization",
                "@id": "https://biznesbezklikania.pl/#organization",
                "name": "BiznesBezKlikania.pl",
                "url": "https://biznesbezklikania.pl/",
                "logo": "https://biznesbezklikania.pl/logo.png",
                "founder": {
                    "@type": "Person",
                    "name": "Bartosz Gaca",
                    "url": "https://bartoszgaca.pl/",
                    "sameAs": [
                        "https://www.linkedin.com/in/bartoszgaca/",
                        "https://github.com/gacabartosz"
                    ]
                },
                "contactPoint": {
                    "@type": "ContactPoint",
                    "telephone": "+48 791 581 239",
                    "contactType": "customer service",
                    "email": "biznesbezklikania@gmail.com",
                    "availableLanguage": ["Polish", "English"]
                },
                "sameAs": [
                    "https://bartoszgaca.pl/",
                    "https://www.instagram.com/biznesbezklikania/",
                    "https://www.facebook.com/biznesbezklikania/"
                ]
            },
            {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "@id": "https://biznesbezklikania.pl/#website",
                "url": "https://biznesbezklikania.pl/",
                "name": "BiznesBezKlikania.pl",
                "description": "Promptowy biznes — bez klikania, bez kodu. 50+ gotowych promptów AI dla polskich MŚP.",
                "publisher": { "@id": "https://biznesbezklikania.pl/#organization" },
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://biznesbezklikania.pl/szukaj?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                },
                "inLanguage": "pl-PL"
            }
        ];

        // Remove old single-schema element if it exists (from earlier version).
        const oldSingle = document.getElementById('organization-schema');
        if (oldSingle) oldSingle.remove();

        // Inject all schemas as one script tag (array form — Google parses both).
        let element = document.getElementById('site-schemas-bundle') as HTMLScriptElement | null;
        if (!element) {
            element = document.createElement('script');
            element.id = 'site-schemas-bundle';
            document.head.appendChild(element);
        }
        element.type = 'application/ld+json';
        element.innerHTML = JSON.stringify(schemas);
    }, []);

    return null;
};


const AppContent: React.FC = () => {
    const location = ReactRouterDOM.useLocation();
    const { theme } = useTheme();

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    React.useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    // Capture UTM/click-id params on first landing for lead attribution (60d localStorage).
    React.useEffect(() => {
        captureUtmOnce();
    }, []);
    
    const isAdmin = location.pathname.startsWith('/admin');

    // Strony lądowania renderują się BEZ oprawy serwisu: bez nagłówka z nawigacją, bez stopki,
    // bez sekcji doklejanych do każdej podstrony.
    //
    // 2026-08-12, wychwycone dopiero na zrzucie z przeglądarki (curl tego nie pokazuje, bo strona
    // renderuje się skryptem): /tiktok dostawało pełną oprawę — hamburger z całą nawigacją, sekcję
    // „Masz konkretny cel (KPI)…" z własnym wezwaniem i stopkę z około 25 odnośnikami. Strona
    // lądowania ma jedno zadanie: zebrać kontakt od widza, który przyszedł z konkretnego filmu.
    // Dwadzieścia wyjść to dwadzieścia sposobów, żeby go nie zebrać.
    const isBareLanding = BARE_LANDING_ROUTES.includes(location.pathname);

    if (isBareLanding) {
        return (
            <div className="min-h-screen bg-bkg-light dark:bg-bkg-dark text-text-primary-light dark:text-text-primary-dark font-sans">
                <ReactRouterDOM.Routes>
                    <ReactRouterDOM.Route path="/tiktok" element={<TikTokLandingPage />} />
                </ReactRouterDOM.Routes>
                {/* Banera zgód NIE renderujemy tutaj — dostarcza go powłoka HTML (public/index.html),
                    ta wpięta w Consent Mode. Patrz komentarz przy imporcie CookieBanner. */}
            </div>
        );
    }

    if (isAdmin) {
        return (
            <ReactRouterDOM.Routes>
                <ReactRouterDOM.Route path="/admin" element={<AdminLayout />}>
                    <ReactRouterDOM.Route index element={<ReactRouterDOM.Navigate to="/admin/leady" replace />} />
                    <ReactRouterDOM.Route path="login" element={<AdminLoginPage />} />
                    <ReactRouterDOM.Route path="leady" element={<AdminLeadsPage />} />
                    <ReactRouterDOM.Route path="inbox" element={<AdminInboxPage />} />
                    <ReactRouterDOM.Route path="settings" element={<AdminSettingsPage />} />
                    <ReactRouterDOM.Route path="mediaplan" element={<MediaplanPage />} />
                    <ReactRouterDOM.Route path="harmonogram" element={<HarmonogramPage />} />
                    <ReactRouterDOM.Route path="propozycje-postow" element={<PropozycjePostowPage />} />
                    <ReactRouterDOM.Route path="posty" element={<PostyFbIgPage />} />
                    <ReactRouterDOM.Route path="aktywnosc" element={<AktywnoscPage />} />
                    <ReactRouterDOM.Route path="reklamy" element={<PlaceholderPage title="Reklamy" phase="Phase 4" eta="tyg 7-8" description="Tworzenie i optymalizacja kampanii Meta Ads + TikTok Ads. L4 ads-autopilot rules-first (pause CPA > 2× target po ≥50 clicks; scale +20% gdy CPA < 0.7× target). Włączany gdy spend ≥ 1500 PLN/mc." integrations={["Meta Marketing API", "TikTok Marketing API", "ads + ad_rule_decisions tables"]} />} />
                    <ReactRouterDOM.Route path="ga4" element={<PlaceholderPage title="GA4" phase="Phase 3" eta="tyg 5-6" description="Konwersje, top landing pages, attribution paths z GA4 Data API. Realtime widget + 28d funnel." integrations={["ga4-reporting-mcp (NEW)", "GA4 Property + service account"]} />} />
                    <ReactRouterDOM.Route path="gsc" element={<PlaceholderPage title="GSC (Search Console)" phase="Phase 3" eta="tyg 5-6" description="Top queries × CTR × position. Auto-suggest meta title/description rewrites dla niskoperformujących URL'i." integrations={["gsc-mcp (NEW)", "GSC OAuth"]} />} />
                    <ReactRouterDOM.Route path="analytics" element={<PlaceholderPage title="Analytics" phase="Phase 5" eta="tyg 9+" description="Cross-channel agregator: GA4 + Plausible + Meta Insights + TikTok Insights + lead funnel. Tygodniowy raport L7." integrations={["wszystkie MCP raportujące"]} />} />
                </ReactRouterDOM.Route>
            </ReactRouterDOM.Routes>
        );
    }

    return (
        <div className="flex flex-col min-h-screen text-text-primary-light dark:text-text-primary-dark font-sans pb-24 sm:pb-16">
            <SiteSchema />
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-0 focus:left-0 focus:bg-white dark:focus:bg-gray-800 focus:p-4 focus:text-blue-600 dark:focus:text-blue-400">
                Przejdź do głównej treści
            </a>
            <Header />
            <main id="main-content" className="flex-grow pt-[64px]">
                <ReactRouterDOM.Routes>
                    <ReactRouterDOM.Route path="/" element={<HomePage />} />
                    <ReactRouterDOM.Route path="/o-nas" element={<AboutPage />} />
                    <ReactRouterDOM.Route path="/kontakt" element={<ContactPage />} />
                    <ReactRouterDOM.Route path="/case-studies" element={<CaseStudiesPage />} />
                    <ReactRouterDOM.Route path="/produkty" element={<ProduktyPage />} />

                    {/* Agenci & Obsługa Klienta */}
                    <ReactRouterDOM.Route path="/rozwiazania/agent-whatsapp-ai" element={<AgentWhatsAppAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/smartclaim-ai" element={<SmartClaimAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/legal-cloud-ai" element={<LegalCloudAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/zapytaj-chata-ai" element={<ZapytajChataAiPage />} />

                    {/* Leady & Rynek */}
                    <ReactRouterDOM.Route path="/rozwiazania/biznesfinder-ai" element={<BiznesFinderAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/carhunter-ai" element={<CarHunterAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/sizehunter-ai" element={<SizeHunterAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/newshunter-ai" element={<NewsHunterAiPage />} />

                    {/* Content & Marketing */}
                    <ReactRouterDOM.Route path="/rozwiazania/contentflow-ai" element={<ContentFlowAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/visual-generator-ai" element={<VisualGeneratorAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/amazon-lister-ai" element={<AmazonListerAiPage />} />

                    {/* Finanse & Integracje */}
                    <ReactRouterDOM.Route path="/rozwiazania/paypal-sync-ai" element={<PayPalSyncAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/integracje" element={<IntegracjePage />} />
                    
                    {/* Core & Specjalne Projekty */}
                    <ReactRouterDOM.Route path="/rozwiazania/perpetuum-ai" element={<PerpetuumAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/gridmaster-ai" element={<GridMasterAiPage />} />
                    <ReactRouterDOM.Route path="/rozwiazania/windsynergy-ai" element={<WindSynergyAiPage />} />
                    
                    {/* Edukacja & Społeczność */}
                    <ReactRouterDOM.Route path="/edukacja/szkolenie-ai-kancelarie" element={<SzkolenieAiKancelariePage />} />
                    <ReactRouterDOM.Route path="/edukacja/kalkulator-roi-ai" element={<ROICalculatorPage />} />
                    <ReactRouterDOM.Route path="/edukacja/ai-quiz" element={<AiQuizPage />} />
                    <ReactRouterDOM.Route path="/spolecznosc/faq" element={<FaqPage />} />
                    
                    {/* Zasoby i Lead Magnety */}
                    <ReactRouterDOM.Route path="/lead-magnet/15-pomyslow-ai" element={<LeadMagnet15PomyslowAiPage />} />
                    {/* Ruch z linku w bio TikToka. Osobna trasa, żeby dało się zmierzyć, ile wejść
                        i leadów pochodzi realnie z kanału, zamiast zgadywać po stronie głównej. */}
                    <ReactRouterDOM.Route path="/tiktok" element={<TikTokLandingPage />} />
                    <ReactRouterDOM.Route path="/szablony-n8n" element={<SzablonyN8nPage />} />
                    <ReactRouterDOM.Route path="/prompty" element={<PromptyPage />} />
                    <ReactRouterDOM.Route path="/prompty/:slug" element={<PromptyDetailPage />} />

                    {/* Catch-all route for 404 Not Found - musi być ostatnia! */}
                    <ReactRouterDOM.Route path="*" element={<NotFoundPage />} />
                </ReactRouterDOM.Routes>
            </main>
            <Footer />
            <WhatsAppButton />
            <ChatWidget />
            <CtaBar />
        </div>
    );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
        <ReactRouterDOM.BrowserRouter>
            <AppContent />
        </ReactRouterDOM.BrowserRouter>
    </ThemeProvider>
  );
};

export default App;